import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useContext, useEffect, useState } from 'react'
import type { ChangeEvent } from 'react'
import dayjs from 'dayjs'
import styled from 'styled-components'

import { BanksArr, QUERY_KEY } from '../../constants'
import type { BankNames } from '../../constants'

import { toast } from '../../utils'

import { StoresContext } from '../../provider/StoreProvider'

import { useAlert } from '../../hooks'

import { getDepositAccount, withdrawalDepositAccount } from '../../api/depositAccount'
import { getWithdrawalAccount, getUserWithdrawalFee } from '../../api/withdrawalAccount'
import { getAssetSummary } from '../../api/my'

import { Wrap } from '../../components/container/Wrap'
import { Content } from '../../components/container/Content'
import { RowBetween, AlignCenterItem, ColumnItem, CenterWrap } from '../../components/common'
import type { BankName } from '../../components/bankIcon/BankIcon'
import type { SecurityName } from '../../components/securityIcon/SecurityIcon'
import { BankIcon } from '../../components/bankIcon'
import { Button } from '../../components/button'
import { Input } from '../../components/input'
import { SecurityIcon } from '../../components/securityIcon'
import { Text } from '../../components/text'
import { DivideLine } from '../../components/divide'
import { Footer } from '../../components/footer'
import { Header } from '../../components/header'
import { Spinner } from '../../components/spinner/Spinner'
import { NoticeContainer } from '../../components/noticeContainer/NoticeContainer'
import { RowData } from './components'

import { Color } from '../../theme/color'

export function AccountWithdrawal() {
  const {
    commonStore: { setShowTab, setIsLoading },
  } = useContext(StoresContext)

  const [withdrawalBalance, setWithdrawalBalance] = useState<number>(0)

  const queryClient = useQueryClient()
  const openAlert = useAlert()

  const depositAccount = useQuery({ queryKey: [QUERY_KEY.depositAccount], queryFn: getDepositAccount })
  const withdrawalAccount = useQuery({ queryKey: [QUERY_KEY.withdrawalAccount], queryFn: getWithdrawalAccount })
  const withdrawalFee = useQuery({ queryKey: [QUERY_KEY.withdrawalFee], queryFn: getUserWithdrawalFee })
  const assetSummary = useQuery({ queryKey: [QUERY_KEY.assetSummary], queryFn: getAssetSummary })
  const { mutate } = useMutation({
    mutationFn: withdrawalDepositAccount,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: [QUERY_KEY.depositAccount] })
      await queryClient.invalidateQueries({ queryKey: [QUERY_KEY.withdrawalAccount] })
      await queryClient.invalidateQueries({ queryKey: [QUERY_KEY.assetSummary] })

      setIsLoading(false)

      openAlert({
        content: (
          <Text size={16} weight={500} lineHeight={24} $textAlign="center">
            정상 처리 되었어요.
          </Text>
        ),
      })

      setWithdrawalBalance(0)
    },
    onError: (error) => {
      setIsLoading(false)
      toast('예치금 출금에 실패했습니다.', error)
    },
  })

  useEffect(() => {
    setShowTab(false)
  }, [])

  if (
    depositAccount.isPending ||
    depositAccount.isError ||
    withdrawalAccount.isPending ||
    withdrawalAccount.isError ||
    withdrawalFee.isPending ||
    withdrawalFee.isError ||
    assetSummary.isPending ||
    assetSummary.isError
  ) {
    return (
      <Wrap>
        <Header type="SUB" title="출금하기" />
        <CenterWrap>
          <Spinner />
        </CenterWrap>
      </Wrap>
    )
  }

  const depositBalance = depositAccount.data.balance ?? 0
  const fee = withdrawalFee.data.result ?? 0

  const possibleWithdrawal = depositBalance - fee < 0 ? 0 : depositBalance - fee
  const isOver = withdrawalBalance ? possibleWithdrawal < withdrawalBalance : false

  const onWithdrawalBalance = async () => {
    const hour = dayjs().hour()
    const minute = dayjs().minute()

    // 23:40 ~ 00:20
    if ((hour === 23 && minute >= 40) || (hour === 0 && minute <= 20)) {
      openAlert({
        content: (
          <Text size={16} weight={500} lineHeight={24} $textAlign="center">
            은행 점검 시간이에요.
            <br />
            (23:40 ~ 00:20)
          </Text>
        ),
      })
      return
    }

    setIsLoading(true)

    mutate(withdrawalBalance)
  }

  const handleWithdrawalBalance = ({ target: { value } }: ChangeEvent<HTMLInputElement>) => {
    if (Number(value) < 0) return
    setWithdrawalBalance(Number(value.replace(/[^0-9]/gi, '')))
  }

  return (
    <Wrap>
      <Header type="SUB" title="출금하기" />
      <StyledContent backgroundColor="gray_1">
        <WithdrawalAmount>
          <Input
            type="number"
            inputMode="numeric"
            pattern="[0-9]*"
            placeholder="금액을 입력해주세요."
            value={withdrawalBalance ? String(withdrawalBalance).replace(/[^0-9]/gi, '') : ''}
            onChange={handleWithdrawalBalance}
          />
          <ColumnItem $gap={16}>
            <RowData title="출금 가능 금액" content={`${possibleWithdrawal.toLocaleString()}원`} />
            <RowData title="수수료" content={`${fee?.toLocaleString()}원`} />
          </ColumnItem>
          <TotalWithdrawal>
            <RowBetween>
              <Text size={18} weight={600}>
                총 출금 금액
              </Text>
              <Text size={18} weight={600} color="primary_5">
                {(possibleWithdrawal < 1 ? 0 : !withdrawalBalance ? 0 : withdrawalBalance + fee).toLocaleString()}원
              </Text>
            </RowBetween>
            <Text size={10} color="text_secondary">
              (수수료 포함)
            </Text>
            {isOver && (
              <Text size={12} color="error" style={{ marginTop: '10px' }}>
                출금 금액이 보유 예치금보다 커요.
              </Text>
            )}
          </TotalWithdrawal>
        </WithdrawalAmount>
        <DivideLine />
        <WithdrawalAccount>
          <Text size={16} weight={700}>
            등록한 출금계좌
          </Text>
          <RowBetween>
            <AlignCenterItem $gap={16}>
              {BanksArr.includes(withdrawalAccount.data.bankName as BankNames) ? (
                <BankIcon size={46} name={withdrawalAccount.data.bankName as BankName} />
              ) : (
                <SecurityIcon size={46} name={withdrawalAccount.data.bankName as SecurityName} />
              )}
              <Text size={18} weight={600}>
                {withdrawalAccount.data.bankName}
              </Text>
            </AlignCenterItem>
          </RowBetween>
          <RowBetween>
            <Text size={14} weight={600} color="text_secondary">
              계좌번호
            </Text>
            <Text size={14} color="text_secondary">
              {withdrawalAccount.data.account}
            </Text>
          </RowBetween>
          <RowBetween>
            <Text size={14} weight={600} color="text_secondary">
              예금주
            </Text>
            <Text size={14} color="text_secondary">
              {withdrawalAccount.data.bankOwner}
            </Text>
          </RowBetween>
        </WithdrawalAccount>
        <NoticeContainer
          title="안내사항"
          list={
            fee
              ? [
                  '신청하신 금액에서 수수료 1,000원이 제외되고 등록하신 계좌로 입금돼요.',
                  '은행 전산 시스템 상태에 따라 반영되는 시간이 소요될 수 있어요.',
                  '은행 점검 시간(23:40 ~ 00:20)에는 충전 및 출금 신청이 제한될 수 있어요.',
                ]
              : [
                  '은행 전산 시스템 상태에 따라 반영되는 시간이 소요될 수 있어요.',
                  '은행 점검 시간(23:40 ~ 00:20)에는 충전 및 출금 신청이 제한될 수 있어요.',
                ]
          }
        />
      </StyledContent>
      <Footer>
        <Button
          type="primary"
          size="large"
          disabled={withdrawalBalance < 1 || isOver}
          block={true}
          onClick={onWithdrawalBalance}
        >
          출금 신청
        </Button>
      </Footer>
    </Wrap>
  )
}

const StyledContent = styled(Content)`
  > *:first-child {
    flex-shrink: 1;
  }
`

const WithdrawalAmount = styled(ColumnItem)`
  gap: 24px;
  padding: 56px 24px 48px;
  background-color: ${Color.white};
`

const WithdrawalAccount = styled(ColumnItem)`
  gap: 16px;
  padding: 24px 24px 45px;
  background-color: ${Color.white};
`

const TotalWithdrawal = styled(ColumnItem)`
  align-items: flex-end;
  gap: 2px;
  > ${RowBetween} {
    width: 100%;
  }
`
